import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Paperclip, Calendar, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import PhotoUploadChatWrapper from "./PhotoUploadChatWrapper";
import RollingDatePicker from "./RollingDatePicker";
import PrivacySelector from "@/components/timeline/PrivacySelector";

interface ChatInputProps {
  onSendMessage?: (message: string, attachments?: any) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  isCreatingForOtherUser?: boolean;
  timelineOwnerName?: string;
  isOwnTimeline?: boolean;
  // Alternative props for controlled input
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
}

const ChatInput = ({ 
  onSendMessage, 
  disabled = false, 
  placeholder = "Type your message...", 
  className,
  isCreatingForOtherUser = false,
  timelineOwnerName,
  isOwnTimeline = true,
  value: controlledValue,
  onChange: controlledOnChange,
  onSubmit: controlledOnSubmit
}: ChatInputProps) => {
  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [privacyLevel, setPrivacyLevel] = useState<string>('timeline_access');
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use controlled or uncontrolled mode
  const isControlled = controlledValue !== undefined;
  const inputValue = isControlled ? controlledValue : message;
  const handleInputChange = isControlled ? controlledOnChange! : setMessage;

  const handleSend = () => {
    if (isControlled && controlledOnSubmit) {
      controlledOnSubmit();
    } else if (onSendMessage && (inputValue.trim() || photos.length > 0)) {
      onSendMessage(inputValue, {
        photos,
        date: selectedDate,
        privacyLevel
      });
      setMessage("");
      setPhotos([]);
      setSelectedDate(null);
      setPrivacyLevel('timeline_access');
      setShowPhotoUpload(false);
      setShowDatePicker(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [inputValue]);

  return (
    <div className={cn("border-t bg-white p-4 space-y-3", className)}>
      {/* Privacy Level Selector */}
      {!isControlled && (
        <PrivacySelector 
          value={privacyLevel}
          onChange={setPrivacyLevel}
          isCreatingForOtherUser={isCreatingForOtherUser}
          timelineOwnerName={timelineOwnerName}
          isOwnTimeline={isOwnTimeline}
        />
      )}

      {/* Photo Upload Section */}
      {showPhotoUpload && (
        <PhotoUploadChatWrapper
          onPhotosSelected={(data) => {
            setPhotos(data.photos);
            setShowPhotoUpload(false);
          }}
        />
      )}

      {/* Date Picker Section */}
      {showDatePicker && (
        <div className="space-y-2">
          <label className="text-sm font-medium">When did this happen?</label>
          <RollingDatePicker
            onSelect={(dateData) => {
              if (dateData.year) {
                const year = parseInt(dateData.year);
                const month = dateData.month ? parseInt(dateData.month) - 1 : 0;
                const day = dateData.day ? parseInt(dateData.day) : 1;
                setSelectedDate(new Date(year, month, day));
              }
            }}
            showToday={true}
          />
        </div>
      )}

      {/* Message Input Area */}
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            className="min-h-[40px] max-h-[120px] resize-none pr-20"
            rows={1}
          />
          
          {/* Attachment Buttons */}
          {!isControlled && (
            <div className="absolute right-2 bottom-2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowPhotoUpload(!showPhotoUpload)}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <Calendar className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        
        <Button
          onClick={handleSend}
          disabled={disabled || (!inputValue.trim() && photos.length === 0)}
          size="sm"
          className="h-10 px-3"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      {/* Status indicators */}
      {!isControlled && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {photos.length > 0 && (
            <span className="flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </span>
          )}
          {selectedDate && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {selectedDate.toLocaleDateString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatInput;
